import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { mobilePayRequestErrorKey, type MobilePayRequestBlock } from '@/lib/mobilePayRequest';
import { UI_MUTED } from '@/lib/typography';

export function MobilePayRequestConfirm({
  amountLabel,
  patientName,
  block,
  ready,
  busy,
  sentUrl,
  copied = false,
  onCopy,
  onCancel,
  onConfirm,
}: {
  amountLabel: string;
  patientName: string;
  block: MobilePayRequestBlock | null;
  ready: boolean;
  busy: boolean;
  sentUrl?: string | null;
  copied?: boolean;
  onCopy?: (url: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();

  if (sentUrl) {
    return (
      <div className="space-y-3" data-mobilepay-request="pending">
        <div>
          <h6 className="text-sm font-medium text-ds-text">
            {t('invoices.mobilepay_request_pending_title')}
          </h6>
          <p className="mt-1 text-sm text-ds-text">
            {t('invoices.mobilepay_request_pending_body')}
          </p>
          <p className={`mt-2 ${UI_MUTED}`}>
            {t('invoices.mobilepay_request_amount')}: {amountLabel}
          </p>
          <p className={`mt-1 ${UI_MUTED}`}>{t('invoices.mobilepay_request_note')}</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={sentUrl}
            aria-label={t('appointments.payment_choice.pay_link')}
            className="w-full rounded-md border border-ds-line-strong bg-ds-sunk px-3 py-2 text-xs text-ds-text"
          />
          {onCopy ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => onCopy(sentUrl)}>
              {copied
                ? t('appointments.payment_choice.copied')
                : t('appointments.payment_choice.copy_link')}
            </Button>
          ) : null}
        </div>
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-mobilepay-request="confirm">
      <h6 className="text-sm font-medium text-ds-text">
        {t('invoices.mobilepay_request_title')}
      </h6>
      <dl className="space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <dt className={UI_MUTED}>{t('invoices.mobilepay_request_amount')}</dt>
          <dd className="font-medium text-ds-text">{amountLabel}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className={UI_MUTED}>{t('invoices.mobilepay_request_patient')}</dt>
          <dd className="font-medium text-ds-text">{patientName}</dd>
        </div>
      </dl>
      <p className={UI_MUTED}>{t('invoices.mobilepay_request_note')}</p>
      {block ? (
        <p className="rounded-md bg-ds-amber-tint px-3 py-2 text-sm text-ds-amber-ink">
          {t(mobilePayRequestErrorKey(block))}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={onConfirm} disabled={busy || !ready || block !== null}>
          {t('invoices.mobilepay_request_send')}
        </Button>
      </div>
    </div>
  );
}
